import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email, "footer");
      if (res.already_subscribed) {
        toast("You're already on the list.", {
          description: "We'll be in touch.",
        });
      } else {
        toast.success("Welcome to AFRQA™", {
          description: "You've joined the list.",
        });
      }
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#070707] border-t border-[#1a1a1a] pt-24 pb-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Newsletter */}
          <div className="lg:col-span-6">
            <span className="font-label text-[#d4af37]">— Join the List —</span>
            <h3 className="mt-4 font-display text-[clamp(1.8rem,3.6vw,3rem)] leading-tight text-white max-w-md">
              Invitations to{" "}
              <span className="font-editorial italic gold-text-gradient">
                future editions.
              </span>
            </h3>
            <p className="mt-5 text-[#a3a3a3] max-w-md font-light">
              Early access. Future editions.
            </p>
            <form
              onSubmit={onSubmit}
              className="mt-8 max-w-md"
              data-testid="newsletter-form"
            >
              <div className="flex items-end gap-4 border-b border-[#3a3a3a] focus-within:border-[#d4af37] transition-colors">
                <input
                  type="email"
                  required
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email-input"
                  className="afrqa-input border-b-0"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit-btn"
                  className="font-label text-[#d4af37] hover:text-white py-3 px-1 transition-colors disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Subscribe →"}
                </button>
              </div>
            </form>
          </div>

          {/* Links */}
          <div className="lg:col-span-6 grid grid-cols-2 md:grid-cols-3 gap-10">
            <div>
              <span className="font-label text-[#8a8a85]">Shop</span>
              <ul className="mt-5 space-y-3 text-[#cfcfca]">
                <li>
                  <Link to="/collections" className="link-underline">
                    Collections
                  </Link>
                </li>
                <li>
                  <a href="/product/africa-frame" className="link-underline">
                    The Africa Frame™
                  </a>
                </li>
                <li>
                  <a href="/#watch" className="link-underline">
                    The Timepiece (Soon)
                  </a>
                </li>
                <li>
                  <a href="/#packaging" className="link-underline">
                    Packaging
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <span className="font-label text-[#8a8a85]">Studio</span>
              <ul className="mt-5 space-y-3 text-[#cfcfca]">
                <li>
                  <Link to="/about" className="link-underline">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/vision" className="link-underline">
                    Vision
                  </Link>
                </li>
                <li>
                  <Link to="/journal" className="link-underline">
                    Journal
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="link-underline">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    data-testid="footer-contact-link"
                    className="link-underline"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <span className="font-label text-[#8a8a85]">Follow</span>
              <ul className="mt-5 space-y-3 text-[#cfcfca]">
                <li>
                  <a
                    href="https://instagram.com/shopafrqa"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="social-instagram"
                    className="link-underline"
                  >
                    Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://tiktok.com"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="social-tiktok"
                    className="link-underline"
                  >
                    TikTok
                  </a>
                </li>
                <li>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="social-twitter"
                    className="link-underline"
                  >
                    X / Twitter
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact strip */}
        <div
          data-testid="footer-contact-strip"
          className="mt-20 pt-10 border-t border-[#1a1a1a] grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          <div>
            <span className="font-label text-[#8a8a85]">Email</span>
            <p className="mt-3">
              <a
                href="mailto:Afrqa@afrqa.com"
                data-testid="footer-email"
                className="font-display text-white text-lg link-underline"
              >
                Afrqa@afrqa.com
              </a>
            </p>
          </div>
          <div>
            <span className="font-label text-[#8a8a85]">Studio</span>
            <p
              className="mt-3 font-display text-white text-lg"
              data-testid="footer-location"
            >
              Los Angeles, California
            </p>
          </div>
          <div>
            <span className="font-label text-[#8a8a85]">Instagram</span>
            <p className="mt-3">
              <a
                href="https://instagram.com/shopafrqa"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="font-display text-white text-lg link-underline"
              >
                @shopafrqa
              </a>
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#1a1a1a] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-editorial italic text-[#cfcfca] text-xl">
            Designed for{" "}
            <span className="gold-text-gradient not-italic font-display">
              the culture.
            </span>
          </p>
          <p className="font-label text-[#6a6a65]">
            © {year} AFRQA™ Studio. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
